import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
} from '@tanstack/react-query';
import React, { FC } from 'react';
import { Control } from 'react-hook-form';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate } from 'react-router-dom';

import { ChevronDownIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import {
  ComboboxButton,
  ComboboxOptions,
  FormCombobox,
  FormComboboxInput,
  FormComboboxOption,
} from '../../../components/combobox/index';
import Loader from '../../../components/loader/index';
import { Tooltip } from '../../../components/tooltip/index';
import { EventsResponse } from '../../../hooks/event/index';
import { EventHooks, useScreenSize } from '../../../hooks/index';
import { EventTypeBackend, SortByType, ValuationEvent } from '../../../types/events.types';
import { FormSchema } from './convert-modal.validation';

type EventType = {
  id: string;
  issuedShares: number;
  issuedSharesOnStart: number;
  name: string;
  sharePrice: number;
};

export type EventsItemsListProps = {
  control: Control<FormSchema>;
  data: ValuationEvent[];
  fetchNextPage: (
    options?: FetchNextPageOptions,
  ) => Promise<InfiniteQueryObserverResult<InfiniteData<EventsResponse, unknown>, Error>>;
  hasNextPage: boolean;
};

const EventsItemsList: FC<EventsItemsListProps> = ({
  data,
  control,
  hasNextPage,
  fetchNextPage,
}) => {
  const { scrollableTarget } = useScreenSize();
  const navigate = useNavigate();
  return (
    <InfiniteScroll
      className="flex w-full flex-col gap-2"
      dataLength={data.length}
      hasMore={hasNextPage}
      loader={<Loader />}
      next={fetchNextPage}
      scrollableTarget={scrollableTarget}
    >
      {data.length > 0 ? (
        <>
          {data
            .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
            .map(({ name, id, sharePrice, issuedSharesOnStart, issuedShares }, i) => (
              <FormComboboxOption
                className="w-full"
                control={control}
                key={`${name}_${i}`}
                name="stepOne.linkedEvent"
                value={{
                  id,
                  name,
                  sharePrice,
                  issuedSharesOnStart,
                  issuedShares,
                }}
              >
                <Tooltip content={name}>
                  <span className="truncate text-sm text-gray-700">{name}</span>
                </Tooltip>
              </FormComboboxOption>
            ))}
          <FormComboboxOption
            className="w-full"
            control={control}
            name="stepOne.linkedEvent"
            value={{
              id: 'no-valuation',
              name: 'No Valuation Event',
              sharePrice: 0,
              issuedSharesOnStart: 0,
              issuedShares: 0,
            }}
          >
            <span className="truncate text-sm text-gray-700">
              <AppFormattedMessage id="NO_VALUATION_EVENT" />
            </span>
          </FormComboboxOption>
        </>
      ) : (
        <>
          <span className="ml-2 px-2 py-[6px] text-sm font-normal text-gray-700">
            <AppFormattedMessage id="NO_RESULTS_FOUND" />
          </span>
        </>
      )}
      <Button
        className="h-[37px] w-full justify-start rounded bg-brand-25 px-[6px] text-start text-sm font-[450] text-brand-700"
        data-permission-required
        onClick={() => navigate('/events?openAddValuationModal=true')}
        styleType="NONE"
      >
        + <AppFormattedMessage id="ADD_EVENT" />
      </Button>
    </InfiniteScroll>
  );
};

export type EventComboboxProps = {
  companyId: string;
  control: Control<FormSchema>;
};

export const EventCombobox: FC<EventComboboxProps> = ({ control, companyId }) => {
  const {
    events: backendEvents,
    fetchNextPage,
    hasNextPage,
  } = EventHooks.useEvents({
    companyId,
    typesFilter: [SortByType.VALUATION],
  });

  if (!backendEvents) return;
  const filteredEvents = backendEvents.filter(
    (event): event is WithPermissions<ValuationEvent> => event.type === EventTypeBackend.VALUATION,
  );

  return (
    <>
      <FormCombobox className="relative w-full" control={control} name="stepOne.linkedEvent">
        <div className="relative">
          <FormComboboxInput
            className={'bg-gray-900'}
            control={control}
            customValue={(value) => (value as EventType)?.name}
            icon={<ChevronDownIcon className="mt-3" />}
            name="stepOne.linkedEvent"
            placeholder={<AppFormattedMessage id="LINKED_EVENT" />}
            readOnly
          />
          <ComboboxButton className="absolute top-0 z-1 h-full w-full" />
        </div>

        <ComboboxOptions className="w-full overflow-x-hidden">
          <EventsItemsList
            control={control}
            data={filteredEvents.filter(({ sharePrice }) => sharePrice) as ValuationEvent[]}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
          />
        </ComboboxOptions>
      </FormCombobox>
    </>
  );
};
